import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArticleCard from '../components/ArticleCard';
import FeaturedArticlesCarousel from '../components/FeaturedArticlesCarousel';
import SEO from '../components/SEO';
import { articleService } from '../services/articleService';
import { activitiesService } from '../services/activitiesService';
import { conferenceService } from '../services/conferenceService';
import { importantDateService } from '../services/importantDateService';
import { formatDateShort } from '../utils/formatDate';
import { getImageUrl, getImageAlt } from '../utils/imageHelpers';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { Link } from 'react-router-dom';

/**
 * HomePage component
 * Displays featured article, recent articles, upcoming dates, and association overview
 * Requirements: 2.6, 5.4, 14.1, 14.3, 14.6
 */
const HomePage = () => {
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [latestActivities, setLatestActivities] = useState([]);
  const [upcomingDates, setUpcomingDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { lang } = useParams();
  const localizedPath = useLocalizedPath();
  const { t } = useTranslation('common');

  useEffect(() => {
    const fetchHomePageData = async () => {
      try {
        setLoading(true);
        setError(null);
        const locale = lang || 'en';

        // Fetch all data in parallel
        const [featuredResponse, recentResponse, datesResponse, activitiesResponse, featuredConferencesResponse] = await Promise.all([
          articleService.getFeaturedArticles(10, locale),
          articleService.getRecentArticles(9, locale),
          importantDateService.getUpcomingDates(5),
          activitiesService.getAllActivities(locale, { pageSize: 6 }),
          conferenceService.getFeaturedConferences(5),
        ]);

        // Combine featured articles with featured conferences and activities for the carousel
        const allFeatured = [];
        if (featuredResponse.data && featuredResponse.data.length > 0) {
          allFeatured.push(...featuredResponse.data);
        }
        // Add featured conferences to the carousel
        if (featuredConferencesResponse.data && featuredConferencesResponse.data.length > 0) {
          featuredConferencesResponse.data.forEach(conference => {
            const attrs = conference.attributes || conference;
            allFeatured.push({
              id: `conference-${conference.id}`,
              attributes: {
                title: attrs.title,
                slug: attrs.slug,
                excerpt: attrs.description ? attrs.description.replace(/<[^>]*>/g, '').substring(0, 200) : '',
                publishedAt: attrs.startDate,
                featuredImage: attrs.featuredImage,
                isFeatured: true,
                _type: 'conference',
              }
            });
          });
        }
        // Also add featured activities to the carousel
        if (activitiesResponse.data) {
          const featuredActivities = activitiesResponse.data.filter(a => {
            const attrs = a.attributes || a;
            return attrs.isFeatured;
          });
          featuredActivities.forEach(activity => {
            const attrs = activity.attributes || activity;
            allFeatured.push({
              id: `activity-${activity.id}`,
              attributes: {
                title: attrs.title,
                slug: attrs.slug,
                excerpt: attrs.excerpt,
                publishedAt: attrs.publishedAt,
                featuredImage: attrs.featuredImage,
                isFeatured: true,
                _type: 'activity',
              }
            });
          });
        }
        setFeaturedArticles(allFeatured);

        if (recentResponse.data) {
          setRecentArticles(recentResponse.data);
        }
        if (datesResponse.data) {
          setUpcomingDates(datesResponse.data);
        }
        if (activitiesResponse.data) {
          setLatestActivities(activitiesResponse.data);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching homepage data:', err);
        setError(err.message || 'Failed to load homepage content');
        setLoading(false);
      }
    };

    fetchHomePageData();
  }, [lang]);

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Hero skeleton */}
          <div className="mb-12 animate-pulse">
            <div className="bg-gray-300 h-96 rounded-lg"></div>
          </div>

          {/* Content grid skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="h-8 bg-gray-300 rounded w-48 mb-6"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="bg-gray-300 h-64 rounded-lg"></div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-1">
              <div className="h-8 bg-gray-300 rounded w-48 mb-6"></div>
              <div className="bg-gray-300 h-64 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('homepage.unableToLoad')}</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            {t('homepage.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Home"
        description="Vietnamese Association for Science and Technology in Poland - Latest news, conferences, and research collaboration opportunities"
        keywords={['VASTP', 'Vietnamese researchers', 'Poland', 'science', 'technology', 'conferences', 'research']}
      />
      <div className="container mx-auto px-4 py-4">
        {/* Hero Section - Featured Articles Carousel */}
        <section className="mb-6">
          <FeaturedArticlesCarousel articles={featuredArticles} />
        </section>

        {/* Main Content Grid - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Area - Latest News and Association Overview */}
          <div className="lg:col-span-2">
            {/* Latest News Section */}
            <section className="mb-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-gray-900">{t('homepage.latestNews')}</h2>
                <Link to={localizedPath('/news')} className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                  {t('actions.viewAll')} →
                </Link>
              </div>
              
              {recentArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {recentArticles.slice(0, 4).map((article) => (
                    <ArticleCard
                      key={article.id}
                      article={article}
                      variant="standard"
                    />
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <p className="text-gray-600">{t('homepage.noArticles')}</p>
                </div>
              )}
            </section>

            {/* Latest Activities Section */}
            {latestActivities.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold text-gray-900">{t('homepage.latestActivities')}</h2>
                  <Link to={localizedPath('/activities')} className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                    {t('actions.viewAll')} →
                  </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {latestActivities.map((activity) => {
                    const attrs = activity.attributes || activity;
                    const imageUrl = getImageUrl(attrs.featuredImage);
                    const imageAlt = getImageAlt(attrs.featuredImage, attrs.title);
                    return (
                      <Link
                        key={activity.id}
                        to={localizedPath(`/activities/${attrs.slug}`)}
                        className="block group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                      >
                        {/* Thumbnail Image */}
                        {imageUrl && (
                          <div className="relative h-44 overflow-hidden">
                            <img
                              src={imageUrl}
                              alt={imageAlt}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="p-5">
                          <div className="flex items-start justify-between mb-2">
                            <span className="inline-block px-2 py-1 text-xs font-semibold bg-purple-100 text-purple-700 rounded">
                              {attrs.activityType === 'seminar' ? 'Seminar' : attrs.activityType === 'announcement' ? 'Announcement' : 'VASTP Activity'}
                            </span>
                            <span className="text-xs text-gray-500">{formatDateShort(attrs.publishedAt, lang || 'en')}</span>
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors">{attrs.title}</h3>
                          {attrs.excerpt && <p className="text-gray-600 text-sm line-clamp-2">{attrs.excerpt}</p>}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Association Overview Section */}
          </div>

          {/* Sidebar - Upcoming Important Dates */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {t('homepage.upcomingDates')}
              </h2>

              {upcomingDates.length > 0 ? (
                <ul className="space-y-4">
                  {upcomingDates.map((dateItem) => {
                    const dateData = dateItem.attributes || dateItem;
                    const conference = dateData.relatedConference?.data?.attributes || dateData.relatedConference?.attributes;
                    const conferenceSlug = conference?.slug;
                    const activity = dateData.relatedActivity?.data?.attributes || dateData.relatedActivity?.attributes;
                    const activitySlug = activity?.slug;
                    const linkPath = conferenceSlug
                      ? localizedPath(`/conferences/${conferenceSlug}`)
                      : activitySlug
                        ? localizedPath(`/activities/${activitySlug}`)
                        : null;
                    const content = (
                      <li
                        key={dateItem.id}
                        className={`border-l-4 border-primary-500 pl-4 py-2 ${linkPath ? 'cursor-pointer hover:bg-gray-50 rounded-r-lg transition-colors' : ''}`}
                      >
                        <div className="text-sm font-semibold text-primary-600 mb-1">
                          {formatDateShort(dateData.date, lang || 'en')}
                        </div>
                        <div className="font-medium text-gray-900 mb-1">
                          {dateData.label}
                        </div>
                        {dateData.description && (
                          <div className="text-sm text-gray-600">
                            {dateData.description}
                          </div>
                        )}
                      </li>
                    );
                    return linkPath ? (
                      <Link key={dateItem.id} to={linkPath}>
                        {content}
                      </Link>
                    ) : (
                      content
                    );
                  })}
                </ul>
              ) : (
                <p className="text-gray-600 text-sm">
                  {t('homepage.noUpcomingDates')}
                </p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
